import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type { TrackId } from 'types/track'
import {
  loadBatch,
  loadNestedTracks,
} from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import { NESTING_RESET } from '@disco/constants/ActionTypes'
import { resetManageNestingForm } from '@disco/state-management/reducers/forms/manage-nesting'
import { getNestingById } from '@disco/state-management/selectors/nesting'
import { getRoutingPreviousLocation } from '@disco/state-management/selectors/routing'
import {
  getTrack,
  getTracksByIds,
} from '@disco/state-management/selectors/tracks'
import { getModalBestReturnTo } from '@disco/utils/modals'
import { createDefaultStateForNestedSet } from '@disco/utils/nesting'
import ManageNesting from './ManageNesting'
import ManageNestingModalLoader from './modal-loader'
const EMPTY_OBJECT = {}

const ManageNestingContainer = (): React.ReactNode => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const locationState: { mainTrackId?: number; tracksIds?: number[] } =
    location?.state ?? EMPTY_OBJECT
  const previousLocation = useSelector(getRoutingPreviousLocation)
  const returnTo = getModalBestReturnTo(location, previousLocation)
  const dataRefreshRequired = useMemo(() => {
    // if user navigates from a page other than /explorer/tracks modals, we need to refresh the data
    return !(previousLocation?.pathname ?? '').includes('/explorer/tracks')
  }, [previousLocation.pathname])
  const dispatch = useDispatch()
  const onClose = useCallback(() => {
    navigate(returnTo)
  }, [navigate, returnTo])
  const mainTrackId = Number(locationState.mainTrackId || params.trackId)
  // const mainTrackId = Number(props.params.trackId)
  const mainTrack = useSelector(state =>
    getTrack(state, {
      id: mainTrackId,
    }),
  )
  const [mainTrackLoading, setMainTrackLoading] = useState(false)
  const [mainTrackError, setMainTrackError] = useState(null)
  const { loading, error, nested_ids } =
    useSelector(state => getNestingById(state, mainTrackId)) || {}
  const affectedNestedIds: TrackId[] = useMemo(() => {
    let result = []

    if (nested_ids) {
      result.push(...nested_ids)
    }

    if (locationState.tracksIds && Array.isArray(locationState.tracksIds)) {
      result.push(...locationState.tracksIds)
    }

    return result
  }, [locationState.tracksIds, nested_ids])
  const tracks = useSelector(state =>
    getTracksByIds(state, {
      ids: affectedNestedIds,
    }),
  )
  const defaultState = useMemo(
    () => (mainTrack ? createDefaultStateForNestedSet(tracks, mainTrack) : {}),
    [],
  )
  useEffect(() => {
    if (loading) {
      return
    }

    if (!mainTrack) {
      ;(async () => {
        setMainTrackLoading(true)
        setMainTrackError(null)

        try {
          const track = await api.getTrack(mainTrackId)
          dispatch(loadBatch([track]))
        } catch (error) {
          setMainTrackError(error)
        }

        setMainTrackLoading(false)
      })()
    }

    dispatch(loadNestedTracks(mainTrackId))
    return () => {
      dispatch(resetManageNestingForm())
      dispatch({
        type: NESTING_RESET,
      })
    }
  }, [])

  if (!mainTrack || mainTrackError || mainTrackLoading) {
    return (
      <ManageNestingModalLoader
        loading={mainTrackLoading}
        error={mainTrackError}
      />
    )
  }

  return (
    <ManageNesting
      mainTrack={mainTrack}
      nestedIds={affectedNestedIds}
      error={error ? new Error(error) : undefined}
      tracksLoading={loading}
      onClose={onClose}
      defaultState={Boolean(locationState.tracksIds) ? defaultState : undefined}
    />
  )
}

export default ManageNestingContainer
